import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledMinuteInput } from '../Styleds.js';
import { convertToPositiveNumberIfPossible } from '../../utils/numberHelpers.js';
const regExpNumbers = /^[0-9]+$/;
const isInvalidMinute = (minutes: string) => {
  if (minutes === '') return false;
  const numberOnly = regExpNumbers.test(minutes);
  return minutes.length > 2 || !numberOnly || Number.parseInt(minutes, 10) > 60;
};
interface MinutesInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  isFocused: boolean;
  getProps: ControlledDateTimePickerContextT['getProps'];
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  readOnly: boolean;
}
export const MinutesInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    placeholder,
    contextRef,
    isFocused,
    getProps,
    tabIndex,
    readOnly,
  }: MinutesInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly || applyAriaDisabled || disabled || isInvalidMinute(e?.target?.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [applyAriaDisabled, disabled, onChange, readOnly],
    );
    const minutesNum = convertToPositiveNumberIfPossible(value);
    return (
      <StyledMinuteInput
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={(DomElem: HTMLInputElement) => {
          contextRef.current = DomElem;
          if (isFocused) DomElem?.focus?.();
        }}
        tabIndex={tabIndex}
        data-testid={ControlledDateTimePickerDatatestid.TIME_INPUTS.MINUTE}
        placeholder={placeholder}
        aria-label="minutes"
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        getOwnerProps={getProps}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuemin={0}
        aria-valuemax={59}
        // when empty, valuenow falls back to the min (not omitted) so VoiceOver honors
        // aria-valuetext="empty" instead of announcing a computed position percentage
        aria-valuenow={minutesNum === -1 ? 0 : minutesNum}
        aria-valuetext={minutesNum === -1 ? 'empty' : minutesNum}
      />
    );
  },
);
